import { useEffect } from 'react';

import type { ResolvedIsland } from '../../core/content';
import { copy } from '../../core/copy';
import { DvobojKarta, DvobojStrip } from './Dvoboj';
import { formatMinutes } from '../../core/lang';
import {
  TIME_BUDGETS,
  awaitingFeedback,
  earnedSouvenirs,
  islandProgress,
  nextStep,
  pendingRevisions,
  shouldNudgeForFeedback,
  stepOpens,
  unlockedFeatures,
  type Blocker,
  type NextStep,
  type NodeView,
  type TimeBudget,
} from '../../core/rules';
import { Btn, SceneStrip, Win } from '../components';
import { PrimljeniKomentari } from './Komentari';
import {
  act,
  contentOf,
  loadPosadaView,
  openNode,
  openPeer,
  playerOf,
  setBudget,
  nextOffer,
  toast,
  urgentDuelOf,
  useStore,
} from '../store';

/**
 * The screen the app opens on.
 *
 * One card. Whatever is on it is the thing to do next, and there is never a second card competing
 * with it. Everything else in this file exists to make sure the right one thing is chosen — the
 * ordering lives in `nextStep`, not here.
 */
export function Sada() {
  const server = useStore((s) => s.server);
  const budget = useStore((s) => s.budget);
  const offerSkip = useStore((s) => s.offerSkip);
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  /* Before the early return: hooks may not be conditional, and this one used to sit below it. */
  const duel = useStore(urgentDuelOf);
  if (!server) return null;

  const step = nextStep(content, player, budget, offerSkip);
  const waiting = awaitingFeedback(content, player);
  const nudgeable = waiting.filter((view) => shouldNudgeForFeedback(view, Date.now()));
  // A revision that exists but did not fit the chosen time budget. It never disappears — it just
  // stops being the card.
  const deferred = step.kind === 'popravka' ? [] : pendingRevisions(content, player);

  /**
   * A duel takes the card — but never from the member's own revision.
   *
   * GDD §2① says one card, and a duel with a clock on it genuinely is the next step where a lesson
   * is not. A revision is the exception in the other direction: GDD §2③ makes it the most valuable
   * thing on anybody's board, and letting a duel push it off the screen would be this product
   * arguing with its own advice. `urgencyOf` already keeps somebody else's unanswered invitation
   * off the card entirely; this keeps the two things that are both yours in the right order.
   */
  if (duel && step.kind !== 'popravka')
    return (
      <div className="sada">
        <DvobojKarta duel={duel} />
        <TimePicker budget={budget} />
      </div>
    );

  return (
    <div className="sada">
      <StepCard step={step} look={server.member.look} />
      <TimePicker budget={budget} />
      {deferred.length > 0 ? <RevisionStrip view={deferred[0]!} /> : null}
      {waiting.length > 0 && step.kind !== 'popravka' ? (
        <WaitingStrip count={waiting.length} nudge={nudgeable.length > 0} />
      ) : null}
      {/*
        Never alongside the student's own revision. If they have one waiting — on the card or on
        the strip above — that is the most valuable thing on their board (GDD §2③), and offering
        somebody else's work next to it is the app arguing with its own advice. The crew screen
        still has it; this is about what competes for attention on the one screen that opens.
      */}
      {/*
        A duel that is running but is not this member's move — or one that is, when a revision has
        the card. Below it and above the crew: somebody is not waiting on you, but a deadline you
        agreed to is still moving. Suppressed beside a revision for the same reason `PosadaStrip`
        is: nothing competes with the step this product exists to produce.
      */}
      {step.kind !== 'popravka' ? <DvobojStrip /> : null}
      {step.kind !== 'popravka' && deferred.length === 0 ? <PosadaStrip /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ somebody is waiting */

/**
 * „Neko čeka komentar." One person, never a list.
 *
 * GDD §9 promises that fifteen minutes can be spent on somebody else's work, and this is where
 * that promise becomes a button. It is a strip rather than a card because the card is the one
 * thing *you* are doing — but it deliberately outranks nothing: it sits at the bottom, under the
 * revision and under the waiting works, and it shows exactly one person at a time. A queue of
 * forty is the overwhelm this whole product exists to cure, pointed at a new target.
 */
function PosadaStrip() {
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  const view = useStore((s) => s.posadaView);
  const budget = useStore((s) => s.budget);
  const crewOn = unlockedFeatures(content, player).includes('posada');

  useEffect(() => {
    if (crewOn) void loadPosadaView();
  }, [crewOn]);

  if (!crewOn || !view || view.works.length === 0) return null;
  // Offered at every budget, but only *promoted* at fifteen minutes — that is the slot where the
  // design doc says nothing of your own fits and helping somebody is the best use of the time.
  const work = view.works[0]!;
  const node = content.islands.flatMap((island) => island.nodes).find((n) => n.id === work.nodeId);

  return (
    <button className={`waiting-strip crew${budget === 15 ? ' lead' : ''}`} onClick={() => openPeer(work.memberId)}>
      <span className="chip dim">{copy.posada.title.toLowerCase()}</span>
      <span style={{ flex: 1, minWidth: 160, textAlign: 'left' }}>
        <b style={{ fontSize: 13 }}>{copy.posada.someoneWaiting(work.name)}</b>
        <span className="tiny muted" style={{ display: 'block' }}>
          {node?.title ?? ''}
        </span>
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ the card */

function StepCard({ step, look }: { step: NextStep; look: number }) {
  const content = useStore(contentOf);
  const player = useStore(playerOf);

  /**
   * A lock with something to do about it.
   *
   * This branch used to render a title, a sentence and nothing else — no button, no destination —
   * and it is reached exactly when there is nothing open anywhere, so it was the one screen in the
   * product that could tell somebody to stop. Now the free island answers it: one named exercise
   * that raises the works count, with the lock itself demoted to the line underneath.
   */
  if (step.kind === 'brava') {
    if (step.offer)
      return (
        <WorkCard
          view={step.offer}
          revision={false}
          look={look}
          lock={{ blocker: step.blocker, island: step.island }}
        />
      );
    return (
      <Win title={copy.sada.header}>
        <h2 className="title">{copy.sada.lockedTitle}</h2>
        <p className="leads">
          {step.blocker.text}
          {step.blocker.detail ? ` · ${step.blocker.detail}` : ''}
        </p>
        <p className="muted tiny">
          Ostrvo {step.island.name}
          {step.island.goal ? ` — ${step.island.goal}` : ''}
        </p>
      </Win>
    );
  }

  if (step.kind === 'kraj') {
    // Everything on the chain is done, but the free island never runs out — so "you have finished
    // everything" would be false as long as there is still something to make.
    if (step.offer)
      return <WorkCard view={step.offer} revision={false} look={look} lock={null} />;
    return (
      <Win title={copy.sada.header}>
        <h2 className="title">{copy.sada.endTitle}</h2>
        <p className="leads">{copy.sada.endLead}</p>
      </Win>
    );
  }

  // Everything is unlocked, nothing is open, and a work is out for comment. That is not the end
  // of the game — it is waiting on a person, and the card should say so.
  if (step.kind === 'ceka-feedback') {
    const first = step.views[0]!;
    return (
      <Win title={copy.sada.header}>
        <h2 className="title">{copy.sada.waitingTitle}</h2>
        <p className="leads">{copy.sada.waitingLead(first.node.title)}</p>
        <div className="btn-row">
          <a className="btn primary" href={content.communityUrl} target="_blank" rel="noreferrer noopener">
            {copy.sada.askForFeedback}
          </a>
          <Btn variant="ghost" onClick={() => openNode(first.node.id)}>
            {copy.node.gotFeedback}
          </Btn>
        </div>
      </Win>
    );
  }

  if (step.kind === 'nista-ne-staje') {
    return (
      <Win title={copy.sada.header}>
        <h2 className="title">{copy.sada.nothingFitsTitle}</h2>
        <p className="leads">
          {step.shortest
            ? copy.sada.nothingFitsLead(step.budget, formatMinutes(step.shortest.minutes))
            : ''}
        </p>
        <p className="muted tiny" style={{ marginBottom: 14 }}>
          {copy.sada.nothingFitsAlt}
        </p>
        <div className="btn-row">
          <Btn variant="primary" onClick={() => setBudget(null)}>
            {copy.sada.showAll}
          </Btn>
          <a
            className="btn ghost"
            href={content.communityUrl}
            target="_blank"
            rel="noreferrer noopener"
          >
            OTVORI ZAJEDNICU
          </a>
        </div>
      </Win>
    );
  }

  return (
    <WorkCard view={step.view} revision={step.kind === 'popravka'} look={look} lock={null} />
  );
}

/**
 * The card itself: one thing to do, how long it takes, and one button.
 *
 * `lock` is set only when this card is standing in for a locked island — the member has run out of
 * course work and the exercise on offer is what moves the lock. It adds exactly one line, and which
 * line it is depends on whether this single exercise closes the whole debt.
 */
function WorkCard({
  view,
  revision,
  look,
  lock,
}: {
  view: NodeView;
  revision: boolean;
  look: number;
  lock: { blocker: Blocker; island: ResolvedIsland } | null;
}) {
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  const { node, island } = view;
  const progress = islandProgress(island, player);

  // What this step actually unlocks, said in words rather than in a number. The decision lives
  // in the rules (stepOpens), because it is a claim about progress, not a formatting choice —
  // an optional node on a cleared island must never promise to clear it.
  let opens = '';
  if (revision) {
    opens = copy.sada.revisionWhy;
  } else if (lock) {
    /**
     * One key, still. Either this exercise opens the island by itself — say so — or it does not,
     * and then the honest line is the lock, quietly, under the thing that moves it. Never both, and
     * never a second number: `postsFrom` is the works this one node can produce, and `amount` is
     * what the blocker already decided to say.
     */
    const works = node.needsRevision ? 2 : node.posts ? 1 : 0;
    const closes = lock.blocker.kind === 'rad' && works >= (lock.blocker.amount ?? 1);
    opens = closes ? copy.sada.offerOpens(lock.island.name) : '';
  } else {
    const unlocks = stepOpens(content, player, island, node);
    if (unlocks?.kind === 'prelazi') opens = copy.sada.clearsIsland(unlocks.island.name);
    else if (unlocks?.kind === 'sledece') opens = copy.sada.opensNext(unlocks.island.name);
  }

  // The authored task text beats the generic line whenever there is one: "napiši ko si i šta
  // montiraš" is a real instruction, "ovo je veliki zadatak" is a label.
  const lead = revision
    ? copy.sada.revisionLead
    : (node.task ??
      (node.kind === 'veliki'
        ? copy.sada.bossLead
        : node.kind === 'vezba'
          ? copy.sada.exerciseLead
          : node.kind === 'riznica'
            ? copy.sada.treasureLead
            : copy.sada.lessonsLead));

  return (
    <>
      <SceneStrip
        biome={island.biome}
        kind={revision ? 'veliki' : node.kind}
        look={look}
        highlight={revision ? 'gold' : null}
        souvenirs={earnedSouvenirs(content, player)}
      />
      <Win title={copy.sada.header} tone={revision ? 'gold' : undefined}>
        <div className="card-head">
          <span className="chip dim">{island.name}</span>
          {/* A free island has no denominator, and „0 od 0" is worse than nothing at all. */}
          {progress.total > 0 ? (
            <span className="chip">{copy.island.of(progress.done, progress.total)}</span>
          ) : null}
        </div>

        {revision ? <span className="chip gold">{copy.sada.revisionTag}</span> : null}

        <h2 className="title" style={{ marginTop: revision ? 10 : 0 }}>
          {node.title}
        </h2>
        <p className="leads">{lead}</p>

        <p style={{ fontSize: 15, marginBottom: 6 }}>Traje oko {formatMinutes(view.minutes)}</p>
        {opens ? <p className="opens">{opens}</p> : null}
        {lock && !opens ? (
          <p className="muted tiny">{copy.sada.offerLock(lock.blocker.text, lock.island.name)}</p>
        ) : null}

        {/*
          Who unblocked this, and where their words are. It sits on the revision card rather than
          in an inbox because the revision card is where a comment becomes actionable — and because
          `nextStep` already promotes a ready revision to the top, the notification and the next
          step are the same piece of paper. Nothing new to learn, nothing new to check.
        */}
        {revision ? <PrimljeniKomentari nodeId={node.id} /> : null}

        {node.note ? (
          <div className="timeline-note">
            {node.note.tekst}
            {node.note.ko ? <div className="who">— {node.note.ko}</div> : null}
          </div>
        ) : null}

        <div className="btn-row">
          {node.submission === 'nista' ? (
            <>
              {node.skoolUrl ? (
                <a className="btn primary" href={node.skoolUrl} target="_blank" rel="noreferrer noopener">
                  {copy.sada.open}
                </a>
              ) : null}
              <Btn
                variant="ghost"
                onClick={() => {
                  void act(node.id, 'done').then((result) => {
                    if (!result.ok) toast(copy.submit.failed, result.message);
                  });
                }}
              >
                {copy.sada.doneButton}
              </Btn>
            </>
          ) : (
            <Btn variant={revision ? 'gold' : 'primary'} onClick={() => openNode(node.id)}>
              {revision ? copy.sada.reviseButton : copy.sada.postedButton}
            </Btn>
          )}
          {/*
            The whole interface to a catalogue of forty. No cap on it: a re-roll that refuses you is
            a screen whose only content is a restriction, and this product does not have those. It
            walks the same ranking every time rather than shuffling, so two taps always land on the
            third exercise and never on a slot machine.
          */}
          {island.free && !revision ? (
            <Btn variant="ghost" size="small" onClick={nextOffer}>
              {copy.sada.offerAnother}
            </Btn>
          ) : null}
          <Btn variant="ghost" size="small" onClick={() => openNode(node.id)}>
            {copy.sada.details}
          </Btn>
        </div>
      </Win>
    </>
  );
}

/* ------------------------------------------------------------------ time picker */

function TimePicker({ budget }: { budget: TimeBudget }) {
  return (
    <Win>
      <p className="lbl">{copy.sada.timeLabel}</p>
      <div className="timerow" role="group" aria-label={copy.sada.timeLabel}>
        {TIME_BUDGETS.map((option) => (
          <button
            key={option.minutes}
            className={`btn small ${budget === option.minutes ? 'primary' : 'ghost'}`}
            aria-pressed={budget === option.minutes}
            onClick={() => setBudget(option.minutes)}
          >
            {option.label}
          </button>
        ))}
        <button
          className={`btn small ${budget === null ? 'primary' : 'ghost'}`}
          aria-pressed={budget === null}
          onClick={() => setBudget(null)}
        >
          {copy.sada.anyTime}
        </button>
      </div>
    </Win>
  );
}

/* ------------------------------------------------------------------ deferred revision */

/**
 * "You have a revision waiting and it needs about an hour." Shown when the student picked a time
 * budget the revision does not fit into, so the most valuable thing on their board stays in front
 * of them without pretending it fits in fifteen minutes.
 */
function RevisionStrip({ view }: { view: NodeView }) {
  return (
    <button className="waiting-strip revision" onClick={() => openNode(view.node.id)}>
      <span className="chip gold">{copy.sada.revisionTag}</span>
      <span style={{ flex: 1, minWidth: 160, textAlign: 'left' }}>
        <b style={{ fontSize: 13 }}>{view.node.title}</b>
        <span className="tiny muted" style={{ display: 'block' }}>
          {copy.sada.revisionDeferred(formatMinutes(view.minutes))}
        </span>
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ waiting strip */

/**
 * Work that is posted and has no comment on it yet. A strip, never a second card — the whole
 * point of this screen is that there is exactly one thing in front of you.
 */
function WaitingStrip({ count, nudge }: { count: number; nudge: boolean }) {
  const content = useStore(contentOf);
  return (
    <div className="waiting-strip">
      <span className="chip dim">čeka</span>
      <div style={{ flex: 1, minWidth: 160 }}>
        <b style={{ fontSize: 13 }}>{copy.sada.waiting(count)}</b>
        {nudge ? <div className="tiny muted">{copy.sada.waitingNudge}</div> : null}
      </div>
      {nudge ? (
        <a className="btn ghost small" href={content.communityUrl} target="_blank" rel="noreferrer noopener">
          {copy.sada.askForFeedback}
        </a>
      ) : null}
    </div>
  );
}
